import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'
import { InputTextarea } from 'primereact/inputtextarea'
import { TabPanel, TabView } from 'primereact/tabview'

import { StatusTag } from '../../../../../../components/tableTemplates'
import { OrderResponse, OrderResponseItem } from '../../../../../../data/models'
import { calculateBreakpointWidth } from '../../../../../../utils/helper/calculateBreakpointWidth'
import {
  ConfigurableComponent,
  ConfigurableTable,
} from '../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../ui-config'
import { useOrderItem } from '../../../../data'

interface Props {
  visible: boolean
  setVisible: (params: boolean) => void
  id: string
  itemId: string
  currency: string
}

export const Info: React.FC<Props> = ({
  visible,
  setVisible,
  id,
  itemId,
  currency,
}) => {
  const { t } = useTranslation()

  const { data, isLoading } = useOrderItem(id, itemId)

  // header
  const header = (
    <>
      <div className="flex align-content-center flex-wrap">
        <span className="mr-3">
          {t('orderResponse.positions.table.info.dialog.title', {
            orderPosition: data?.OrderPosition,
          })}
        </span>

        <StatusTag status={data?.ConfirmationCode} />
      </div>
    </>
  )

  // footer
  const footer = (
    <>
      <Button
        label={t('orderResponse.positions.table.info.dialog.close.label')}
        icon="pi pi-check"
        onClick={() => setVisible(false)}
        autoFocus
        className="mt-3 w-full md:w-auto"
      />
    </>
  )

  return (
    <Dialog
      header={header}
      visible={visible}
      style={{ width: '55rem' }}
      breakpoints={{ [calculateBreakpointWidth(55)]: '90vw' }}
      footer={footer}
      onHide={() => setVisible(false)}
      blockScroll
    >
      <TabView>
        <TabPanel
          header={t('orderResponse.positions.table.info.dialog.general.title')}
        >
          <OrderItemInfos
            currency={currency}
            data={data!}
            isLoading={isLoading}
          />
        </TabPanel>
        <TabPanel
          header={t(
            'orderResponse.positions.table.info.dialog.allowanceCharges.title',
          )}
          disabled={!data?.AllowanceCharges?.length}
        >
          <AllowanceCharges
            data={data?.AllowanceCharges}
            isLoading={isLoading}
          />
        </TabPanel>
      </TabView>
    </Dialog>
  )
}

// =========================
// OrderItemInfos
// =========================
interface OrderItemInfosProps {
  currency: OrderResponse['Currency']
  data: OrderResponseItem
  isLoading: boolean
}

export const OrderItemInfos: React.FC<OrderItemInfosProps> = ({
  currency,
  data,
  isLoading,
}) => {
  const { uiConfig: config } = useConfigContext()
  const { i18n } = useTranslation()
  const { language } = i18n

  // itemId
  const itemIdLabel = <label htmlFor="itemId" className="text-sm"></label>

  const itemIdComponent = (
    <InputText id="itemId" className="w-full" value={data?.ItemId} disabled />
  )

  const itemIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={itemIdComponent}
      label={itemIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationItemId
  const confirmationItemIdLabel = (
    <label htmlFor="confirmationItemId" className="text-sm"></label>
  )

  const confirmationItemIdComponent = (
    <InputText
      id="confirmationItemId"
      className="w-full"
      value={data?.ConfirmationItemId}
      disabled
    />
  )

  const confirmationItemIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationItemIdComponent}
      label={confirmationItemIdLabel}
      isLoading={isLoading}
    />
  )

  // countryOfOrigin
  const countryOfOriginLabel = (
    <label htmlFor="countryOfOrigin" className="text-sm"></label>
  )

  const countryOfOriginComponent = (
    <InputText
      id="countryOfOrigin"
      className="w-full"
      value={data?.CountryOfOrigin}
      disabled
    />
  )

  const countryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={countryOfOriginComponent}
      label={countryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // confirmationCountryOfOrigin
  const confirmationCountryOfOriginLabel = (
    <label htmlFor="confirmationCountryOfOrigin" className="text-sm"></label>
  )

  const confirmationCountryOfOriginComponent = (
    <InputText
      id="confirmationCountryOfOrigin"
      className="w-full"
      value={data?.ConfirmationCountryOfOrigin}
      disabled
    />
  )

  const confirmationCountryOfOriginWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationCountryOfOriginComponent}
      label={confirmationCountryOfOriginLabel}
      isLoading={isLoading}
    />
  )

  // gtin
  const gtinLabel = <label htmlFor="gtin" className="text-sm"></label>

  const gtinComponent = (
    <InputText id="gtin" className="w-full" value={data?.GTIN} disabled />
  )

  const gtinWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={gtinComponent}
      label={gtinLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductId
  const buyerProductIdLabel = (
    <label htmlFor="buyerProductId" className="text-sm"></label>
  )

  const buyerProductIdComponent = (
    <InputText
      id="buyerProductId"
      className="w-full"
      value={data?.BuyerProductId}
      disabled
    />
  )

  const buyerProductIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={buyerProductIdComponent}
      label={buyerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductId
  const confirmationSellerProductIdLabel = (
    <label htmlFor="confirmationSellerProductId" className="text-sm"></label>
  )

  const confirmationSellerProductIdComponent = (
    <InputText
      id="confirmationSellerProductId"
      value={data?.ConfirmationSellerProductId}
      className="w-full"
      disabled
    />
  )

  const confirmationSellerProductIdWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationSellerProductIdComponent}
      label={confirmationSellerProductIdLabel}
      isLoading={isLoading}
    />
  )

  // buyerProductDescription
  const buyerProductDescriptionLabel = (
    <label htmlFor="buyerProductDescription" className="text-sm"></label>
  )

  const buyerProductDescriptionComponent = (
    <InputText
      id="buyerProductDescription"
      className="w-full"
      value={data?.BuyerProductDescription}
      disabled
    />
  )

  const buyerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={buyerProductDescriptionComponent}
      label={buyerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // confirmationSellerProductDescription
  const confirmationSellerProductDescriptionLabel = (
    <label
      htmlFor="confirmationSellerProductDescription"
      className="text-sm"
    ></label>
  )

  const confirmationSellerProductDescriptionComponent = (
    <InputText
      id="confirmationSellerProductDescription"
      value={data?.ConfirmationSellerProductDescription}
      className="w-full"
      disabled
    />
  )

  const confirmationSellerProductDescriptionWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationSellerProductDescriptionComponent}
      label={confirmationSellerProductDescriptionLabel}
      isLoading={isLoading}
    />
  )

  //  orderQuantity
  const orderQuantityLabel = (
    <label htmlFor="orderQuantity" className="text-sm"></label>
  )

  const orderQuantityComponent = (
    <InputNumber
      id="orderQuantity"
      className="w-full"
      value={data?.OrderQuantity}
      locale={language}
      disabled
    />
  )

  const orderQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={orderQuantityComponent}
      label={orderQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationQuantity
  const confirmationQuantityLabel = (
    <label htmlFor="confirmationQuantity" className="text-sm"></label>
  )

  const confirmationQuantityComponent = (
    <InputNumber
      id="confirmationQuantity"
      value={data?.ConfirmationQuantity}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationQuantityComponent}
      label={confirmationQuantityLabel}
      isLoading={isLoading}
    />
  )

  // orderUnit
  const orderUnitLabel = <label htmlFor="orderUnit" className="text-sm"></label>

  const orderUnitComponent = (
    <InputText
      id="orderUnit"
      className="w-full"
      value={data?.OrderUnit}
      disabled
    />
  )

  const orderUnitWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={orderUnitComponent}
      label={orderUnitLabel}
      isLoading={isLoading}
    />
  )

  //  confirmationUnit
  const confirmationUnitLabel = (
    <label htmlFor="confirmationUnit" className="text-sm"></label>
  )

  const confirmationUnitComponent = (
    <InputText
      id="confirmationUnit"
      value={data?.ConfirmationUnit}
      className="w-full"
      disabled
    />
  )

  const confirmationUnitWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationUnitComponent}
      label={confirmationUnitLabel}
      isLoading={isLoading}
    />
  )

  // orderPriceBaseQuantity
  const orderPriceBaseQuantityLabel = (
    <label htmlFor="orderPriceBaseQuantity" className="text-sm"></label>
  )

  const orderPriceBaseQuantityComponent = (
    <InputNumber
      id="orderPriceBaseQuantity"
      className="w-full"
      value={data?.OrderPriceBaseQuantity}
      locale={language}
      disabled
    />
  )

  const orderPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={orderPriceBaseQuantityComponent}
      label={orderPriceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceBaseQuantity
  const confirmationPriceBaseQuantityLabel = (
    <label htmlFor="confirmationPriceBaseQuantity" className="text-sm"></label>
  )

  const confirmationPriceBaseQuantityComponent = (
    <InputNumber
      id="confirmationPriceBaseQuantity"
      value={data?.ConfirmationPriceBaseQuantity}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceBaseQuantityWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationPriceBaseQuantityComponent}
      label={confirmationPriceBaseQuantityLabel}
      isLoading={isLoading}
    />
  )

  // orderPriceUnitOfMeasure
  const orderPriceUnitOfMeasureLabel = (
    <label htmlFor="orderPriceUnitOfMeasure" className="text-sm"></label>
  )

  const orderPriceUnitOfMeasureComponent = (
    <InputText
      id="orderPriceUnitOfMeasure"
      className="w-full"
      value={data?.OrderPriceUnitOfMeasure}
      disabled
    />
  )

  const orderPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={orderPriceUnitOfMeasureComponent}
      label={orderPriceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceUnitOfMeasure
  const confirmationPriceUnitOfMeasureLabel = (
    <label htmlFor="confirmationPriceUnitOfMeasure" className="text-sm"></label>
  )

  const confirmationPriceUnitOfMeasureComponent = (
    <InputText
      id="confirmationPriceUnitOfMeasure"
      value={data?.ConfirmationPriceUnitOfMeasure}
      className="w-full"
      disabled
    />
  )

  const confirmationPriceUnitOfMeasureWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationPriceUnitOfMeasureComponent}
      label={confirmationPriceUnitOfMeasureLabel}
      isLoading={isLoading}
    />
  )

  // orderPrice
  const orderPriceLabel = (
    <label htmlFor="orderPrice" className="text-sm"></label>
  )
  const orderPriceComponent = (
    <InputNumber
      id="orderPrice"
      value={data?.OrderPrice}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderPriceWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={orderPriceComponent}
      label={orderPriceLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPrice
  const confirmationPriceLabel = (
    <label htmlFor="confirmationPrice" className="text-sm"></label>
  )

  const confirmationPriceComponent = (
    <InputNumber
      id="confirmationPrice"
      value={data?.ConfirmationPrice}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationPriceComponent}
      label={confirmationPriceLabel}
      isLoading={isLoading}
    />
  )

  // orderPriceSum
  const orderPriceSumLabel = (
    <label htmlFor="orderPriceSum" className="text-sm"></label>
  )

  const orderPriceSumComponent = (
    <InputNumber
      id="orderPriceSum"
      value={data?.OrderPriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const orderPriceSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={orderPriceSumComponent}
      label={orderPriceSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationPriceSum
  const confirmationPriceSumLabel = (
    <label htmlFor="confirmationPriceSum" className="text-sm"></label>
  )

  const confirmationPriceSumComponent = (
    <InputNumber
      id="confirmationPriceSum"
      value={data?.ConfirmationPriceSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationPriceSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationPriceSumComponent}
      label={confirmationPriceSumLabel}
      isLoading={isLoading}
    />
  )

  // allowanceChargeSum
  const allowanceChargeSumLabel = (
    <label htmlFor="allowanceChargeSum" className="text-sm"></label>
  )

  const allowanceChargeSumComponent = (
    <InputNumber
      id="allowanceChargeSum"
      value={data?.AllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const allowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={allowanceChargeSumComponent}
      label={allowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // confirmationAllowanceChargeSum
  const confirmationAllowanceChargeSumLabel = (
    <label htmlFor="confirmationAllowanceChargeSum" className="text-sm"></label>
  )

  const confirmationAllowanceChargeSumComponent = (
    <InputNumber
      id="confirmationAllowanceChargeSum"
      value={data?.ConfirmationAllowanceChargeSum}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationAllowanceChargeSumWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationAllowanceChargeSumComponent}
      label={confirmationAllowanceChargeSumLabel}
      isLoading={isLoading}
    />
  )

  // lineItemAmount
  const lineItemAmountLabel = (
    <label htmlFor="lineItemAmount" className="text-sm"></label>
  )

  const lineItemAmountComponent = (
    <InputNumber
      id="lineItemAmount"
      value={data?.LineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const lineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={lineItemAmountComponent}
      label={lineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // confirmationLineItemAmount
  const confirmationLineItemAmountLabel = (
    <label htmlFor="confirmationLineItemAmount" className="text-sm"></label>
  )

  const confirmationLineItemAmountComponent = (
    <InputNumber
      id="confirmationLineItemAmount"
      value={data?.ConfirmationLineItemAmount}
      mode="currency"
      currency={currency}
      className="w-full"
      locale={language}
      disabled
    />
  )

  const confirmationLineItemAmountWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationLineItemAmountComponent}
      label={confirmationLineItemAmountLabel}
      isLoading={isLoading}
    />
  )

  // orderDeliveryDate
  const orderDeliveryDateLabel = (
    <label htmlFor="orderDeliveryDate" className="text-sm"></label>
  )

  const orderDeliveryDate = data?.OrderDeliveryDate
    ? new Date(data?.OrderDeliveryDate)
    : null

  const orderDeliveryDateComponent = (
    <Calendar
      id="orderDeliveryDate"
      value={orderDeliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const orderDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={orderDeliveryDateComponent}
      label={orderDeliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationDeliveryDate
  const confirmationDeliveryDateLabel = (
    <label htmlFor="confirmationDeliveryDate" className="text-sm"></label>
  )

  const confirmationDeliveryDate = data?.ConfirmationDeliveryDate
    ? new Date(data?.ConfirmationDeliveryDate)
    : null

  const confirmationDeliveryDateComponent = (
    <Calendar
      id="confirmationDeliveryDate"
      value={confirmationDeliveryDate}
      className="w-full"
      showIcon
      locale={language}
      disabled
    />
  )

  const confirmationDeliveryDateWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationDeliveryDateComponent}
      label={confirmationDeliveryDateLabel}
      isLoading={isLoading}
    />
  )

  // confirmationReasonCode
  const confirmationReasonCodeLabel = (
    <label htmlFor="confirmationReasonCode" className="text-sm"></label>
  )

  const confirmationReasonCodeComponent = (
    <Dropdown
      id="confirmationReasonCode"
      className="w-full"
      value={data?.ConfirmationReason?.Code}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const confirmationReasonCodeWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationReasonCodeComponent}
      label={confirmationReasonCodeLabel}
      isLoading={isLoading}
    />
  )

  // confirmationReasonDescription
  const confirmationReasonDescriptionLabel = (
    <label htmlFor="confirmationReasonDescription" className="text-sm"></label>
  )

  const confirmationReasonDescriptionComponent = (
    <InputText
      id="confirmationReasonDescription"
      value={data?.ConfirmationReason?.Description}
      className="w-full"
      disabled
    />
  )

  const confirmationReasonDescriptionWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={confirmationReasonDescriptionComponent}
      label={confirmationReasonDescriptionLabel}
      isLoading={isLoading}
    />
  )

  // notes
  const notesLabel = <label htmlFor="notes" className="text-sm"></label>

  const notesComponent = (
    <InputTextarea
      id="notes"
      rows={5}
      value={data?.Notes}
      className="w-full"
      disabled
    />
  )

  const notesWrapper = (
    <ConfigurableComponent
      config={config.orderResponse.infoOrderItem}
      component={notesComponent}
      label={notesLabel}
      isLoading={isLoading}
    />
  )

  return (
    <>
      {/* itemId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{itemIdWrapper}</div>
        <div className="col-12 md:col-6">{confirmationItemIdWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* countryOfOrigin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{countryOfOriginWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationCountryOfOriginWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* gtin */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{gtinWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductId */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductIdWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductIdWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* buyerProductDescription */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{buyerProductDescriptionWrapper}</div>
        <div className="col-12 md:col-6">
          {confirmationSellerProductDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* quantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderQuantityWrapper}</div>

        <div className="col-12 md:col-6">{confirmationQuantityWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* unit */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderUnitWrapper}</div>

        <div className="col-12 md:col-6">{confirmationUnitWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* priceBaseQuantity */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceBaseQuantityWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceBaseQuantityWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* priceUnitOfMeasure */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceUnitOfMeasureWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationPriceUnitOfMeasureWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* price */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* confirmationPriceSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderPriceSumWrapper}</div>

        <div className="col-12 md:col-6">{confirmationPriceSumWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* allowanceChargeSum */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{allowanceChargeSumWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationAllowanceChargeSumWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* ItemAmount */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{lineItemAmountWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationLineItemAmountWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* deliveryDate */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{orderDeliveryDateWrapper}</div>

        <div className="col-12 md:col-6">{confirmationDeliveryDateWrapper}</div>
      </div>

      <Divider className="md:hidden" />

      {/* code */}
      <div className="formgrid grid">
        <div className="col-12 md:col-6">{confirmationReasonCodeWrapper}</div>

        <div className="col-12 md:col-6">
          {confirmationReasonDescriptionWrapper}
        </div>
      </div>

      <Divider className="md:hidden" />

      {/* notes */}
      <div className="formgrid grid">
        <div className="col-12">{notesWrapper}</div>
      </div>
    </>
  )
}

// =========================
// AllowanceCharges
// =========================
interface AllowanceChargesProps {
  data: OrderResponseItem['AllowanceCharges']
  isLoading?: boolean
}

export const AllowanceCharges: React.FC<AllowanceChargesProps> = ({
  data,
  isLoading,
}) => {
  const { uiConfig: config } = useConfigContext()

  return (
    <ConfigurableTable
      config={config.orderResponse.infoOrderItem.allowanceCharges.table}
      data={data}
      isLoading={isLoading}
      stateKey="table-info-order-item"
    />
  )
}
